import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Autoplay, Pagination } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getPopularsListServices } from '../../redux/slices/common'
import GradeIcon from '@mui/icons-material/Grade'
import { pathNameServiceTypeMapping } from '../../utils/serviceTypes'
import { Skeleton, Box, Typography, Grid, Stack } from '@mui/material'
import { MoveRight } from 'lucide-react'

const linkMap = {
  activities:
    '/activities/activities?cityName=All&name=&destinationName=All&productId=',
  holidays:
    '/holidays/holidays?country=&cityName=All&name=&adult=1&child=0&rooms=1&productId=',
  staycation:
    '/staycation/staycations?cityName=All&name=&checkIn=&checkOut=&productId=',
  bus: '/busBooking/busRentals?cityName=All&name=All&pickupDateTime=2025-04-04&pickupDateTimecheckOutDate=2025-04-05&pickupTimeValue=&productId=&type=All'
}

const PopularDestinations = ({ router, pageName, title = 'Trending' }) => {
  const dispatch = useDispatch()
  const pathname = usePathname()
  const [services, setServices] = useState([])
  const { popularList } = useSelector(state => state.common)
  const [isPopularDisabled, setIsPopularDisabled] = useState(false)
  const [loader, setLoader] = useState(true)

  useEffect(() => {
    const isFlightOrHotels = pageName === 'flights' || pageName === 'hotels'
    const serviceType = pathNameServiceTypeMapping[pageName]
    if (!isFlightOrHotels) {
      setLoader(true)
      dispatch(getPopularsListServices(serviceType))
    }
    setIsPopularDisabled(isFlightOrHotels)
  }, [router.asPath])

  useEffect(() => {
    if (popularList && popularList !== undefined) {
      setLoader(false)
      setServices(popularList?.responseData)
    }
  }, [popularList])

  const truncateText = (text, maxLength) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  const typeKey = useMemo(() => {
    if (typeof pathname !== 'string') return null;

    if (pathname.includes('/activities')) return 'activities'
    if (pathname.includes('/holidays')) return 'holidays'
    if (pathname.includes('/staycation')) return 'staycation'
    if (pathname.includes('/bus')) return 'bus'
    return null
  }, [pathname])

  const dynamicHref = typeKey ? linkMap[typeKey] : '#'

  if (isPopularDisabled) {
    return null
  }

  return loader ? (
    <div className='mt-2 mt-md-5 mb-0'>
      <Typography variant='h6' sx={{ marginBottom: 2 }}>
        <Skeleton variant='rectangular' width={200} height={40} />
      </Typography>
      <Grid container spacing={2}>
        {[...Array(4)].map((_, index) => (
          <Grid item xs={3} key={index}>
            <Skeleton variant='rectangular' width='100%' height={180} />
            {/* <Skeleton variant="text" width="60%" sx={{ marginTop: 1 }} /> */}
          </Grid>
        ))}
      </Grid>
    </div>
  ) : (
    <div className='popular-slider-container mt-2 mt-md-5 mb-0'>
      {/* 🔹 Section Title */}
      <div className='d-flex items-center justify-between'>
        <h2 className='slider-title'>{title}</h2>
        {typeKey && (
          <Link
            href={dynamicHref}
            className='link-body-emphasis link-offset-2 link-underline-opacity-25 link-underline-opacity-75-hover inline-flex items-center gap-1'
            style={{
              cursor: 'pointer'
            }}
          >
            VIEW MORE <MoveRight size={16} />
          </Link>
        )}
      </div>
      <hr className='slider-divider' />

      <Swiper
        modules={[Navigation, Autoplay]}
        slidesPerView={3}
        spaceBetween={30}
        autoplay={{ delay: 3000, disableOnInteraction: false }}
        speed={1000}
        navigation
        pagination={false}
        className='popular-slider'
        breakpoints={{
          1024: { slidesPerView: 3 },
          768: { slidesPerView: 3 },
          480: { slidesPerView: 1 },
          320: { slidesPerView: 1 }
        }}
      >
        {services?.map((item, index) => {
          const typePaths = {
            4: '/staycation/staycation-details',
            6: '/activities/activity-details',
            7: '/busrental/bus-details',
            8: '/holidaypackage/holiday-details',
            9: '/carrentalnew/car-details'
          }

          const getTypePath = type => {
            return typePaths[type] || ''
          }

          return (
            <SwiperSlide key={index} className='popular-slide'>
              <Link
                href={`${getTypePath(item.type)}?_id=${item?._id}`}
                className='destination-card'
              >
                {/* 🔹 Image */}
                <Image
                  width={300}
                  height={300}
                  alt={item.name}
                  src={`${process.env.IMAGE_BASE_URL}/${item?.image}`}
                  className='destination-image'
                  onError={e => (e.currentTarget.src = '/fallback-image.jpg')}
                />

                {/* 🔹 Content */}
                <div className='destination-content'>
                  <h3 className='destination-title'>
                    {truncateText(item.name, 30)}
                  </h3>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <GradeIcon className='text-dark-3' fontSize='small' />
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      {item?.rating}
                    </span>
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      {item?.reviews ? `(${item?.reviews})` : '-'}
                    </span>
                  </div>
                </div>
              </Link>
            </SwiperSlide>
          )
        })}
      </Swiper>
    </div>
  )
}

export default PopularDestinations
